import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../utils/constants';
import { userAPI } from '../services/api';

import { useLanguage } from '../context/LanguageContext';
import {
  MessageSquare, Send, Plus, X, Clock, CheckCircle,
  AlertCircle, ChevronLeft, HelpCircle, FileText
} from 'lucide-react';

const SupportPage = () => {
  const { t, language } = useLanguage();
  
  const statusColors = {
    open: '#3b82f6',
    in_progress: COLORS.primary,
    resolved: COLORS.success,
    closed: COLORS.textSecondary,
  };

  const getStatusLabel = (status) => {
    const labels = {
      ru: { open: 'Открыт', in_progress: 'В работе', resolved: 'Решён', closed: 'Закрыт' },
      en: { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' },
      kk: { open: 'Ашық', in_progress: 'Жұмыста', resolved: 'Шешілді', closed: 'Жабық' },
    };
    return labels[language]?.[status] || status;
  };

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadTickets();
    const interval = setInterval(loadTickets, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedTicket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTickets = async () => {
    try {
      const res = await userAPI.getMyTickets();
      setTickets(res.data || []);
      
      // Update selected ticket if exists
      if (selectedTicket) {
        const updated = res.data?.find(t => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) return;
    
    setSending(true);
    try {
      await userAPI.createTicket({
        subject: newTicket.subject,
        message: newTicket.message,
      });
      setNewTicket({ subject: '', message: '' });
      setShowNewTicket(false);
      loadTickets();
    } catch (err) {
      console.error('Failed to create ticket:', err);
      alert('Ошибка при создании тикета');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      await userAPI.replyToTicket(selectedTicket.id, replyText);
      setReplyText('');
      loadTickets();
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSending(false);
    }
  };

  const selectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyText('');
  };

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    // Show ticket detail
    if (selectedTicket) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: 'calc(100vh - 140px)',
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px',
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <button
              onClick={() => setSelectedTicket(null)}
              style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer' }}
            >
              <ChevronLeft size={24} color={COLORS.text} />
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600' }}>
                {selectedTicket.subject}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '10px',
                  backgroundColor: `${statusColors[selectedTicket.status]}20`,
                  color: statusColors[selectedTicket.status],
                  fontSize: '11px',
                  fontWeight: '600',
                }}>
                  {getStatusLabel(selectedTicket.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
            {selectedTicket.messages?.map((msg, i) => {
              const isUser = msg.sender_role === 'user';
              return (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  marginBottom: '12px',
                }}>
                  <div style={{
                    maxWidth: '85%',
                    padding: '12px 16px',
                    backgroundColor: isUser ? COLORS.primary : COLORS.background,
                    color: isUser ? '#000' : COLORS.text,
                    borderRadius: '16px',
                    borderBottomRightRadius: isUser ? '4px' : '16px',
                    borderBottomLeftRadius: isUser ? '16px' : '4px',
                  }}>
                    {!isUser && (
                      <div style={{ fontSize: '11px', color: '#0ecb81', marginBottom: '4px', fontWeight: '600' }}>
                        Поддержка
                      </div>
                    )}
                    <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                      {msg.message}
                    </div>
                    <div style={{ fontSize: '10px', opacity: 0.7, textAlign: 'right', marginTop: '4px' }}>
                      {new Date(msg.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Input */}
          {selectedTicket.status !== 'closed' && (
            <div style={{
              padding: '12px 16px',
              borderTop: `1px solid ${COLORS.border}`,
              display: 'flex',
              gap: '10px',
            }}>
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Напишите сообщение..."
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '24px',
                  color: COLORS.text,
                  fontSize: '14px',
                }}
                onKeyPress={(e) => e.key === 'Enter' && sendReply()}
              />
              <button
                onClick={sendReply}
                disabled={!replyText.trim() || sending}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: replyText.trim() ? COLORS.primary : COLORS.border,
                  border: 'none',
                  cursor: replyText.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Send size={20} color={replyText.trim() ? '#000' : COLORS.textSecondary} />
              </button>
            </div>
          )}

          {selectedTicket.status === 'closed' && (
            <div style={{
              padding: '16px',
              textAlign: 'center',
              color: COLORS.textSecondary,
              fontSize: '13px',
              borderTop: `1px solid ${COLORS.border}`,
            }}>
              Тикет закрыт
            </div>
          )}
        </div>
      );
    }

    // Show new ticket form
    if (showNewTicket) {
      return (
        <div style={{ paddingBottom: '100px' }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
          }}>
            <button
              onClick={() => setShowNewTicket(false)}
              style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer' }}
            >
              <ChevronLeft size={24} color={COLORS.text} />
            </button>
            <span style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600' }}>
              Новое обращение
            </span>
          </div>

          {/* Form */}
          <div style={{
            backgroundColor: COLORS.card,
            borderRadius: '16px',
            padding: '20px',
          }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
                Тема обращения
              </label>
              <input
                type="text"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Кратко опишите проблему"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '12px',
                  color: COLORS.text,
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
                Сообщение
              </label>
              <textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                placeholder="Подробно опишите вашу проблему или вопрос..."
                rows={6}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '12px',
                  color: COLORS.text,
                  fontSize: '14px',
                  resize: 'none',
                }}
              />
            </div>

            <button
              onClick={createTicket}
              disabled={!newTicket.subject.trim() || !newTicket.message.trim() || sending}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: (newTicket.subject.trim() && newTicket.message.trim()) ? COLORS.primary : COLORS.border,
                color: (newTicket.subject.trim() && newTicket.message.trim()) ? '#000' : COLORS.textSecondary,
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: (newTicket.subject.trim() && newTicket.message.trim()) ? 'pointer' : 'not-allowed',
              }}
            >
              {sending ? t('loading') : t('submit')}
            </button>
          </div>
        </div>
      );
    }

    // Show tickets list
    return (
      <div style={{ paddingBottom: '100px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <span style={{ color: COLORS.text, fontSize: '20px', fontWeight: '700' }}>
            Поддержка
          </span>
          <button
            onClick={() => setShowNewTicket(true)}
            style={{
              padding: '10px 16px',
              backgroundColor: COLORS.primary,
              color: '#000',
              border: 'none',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Plus size={18} />
            Новое
          </button>
        </div>

        {/* Help Card */}
        <div style={{
          backgroundColor: '#1e90ff15',
          border: '1px solid #1e90ff30',
          borderRadius: '14px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}>
          <HelpCircle size={24} color="#1e90ff" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
              Нужна помощь?
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: '13px', lineHeight: '1.5' }}>
              Создайте обращение и наша команда ответит в ближайшее время. Среднее время ответа: 2 часа.
            </div>
          </div>
        </div>

        {/* Tickets List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
            Загрузка...
          </div>
        ) : tickets.length === 0 ? (
          <div style={{
            backgroundColor: COLORS.card,
            borderRadius: '16px',
            padding: '40px 20px',
            textAlign: 'center',
          }}>
            <MessageSquare size={48} color={COLORS.textSecondary} style={{ marginBottom: '16px' }} />
            <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
              Нет обращений
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: '14px', marginBottom: '20px' }}>
              У вас пока нет активных обращений в поддержку
            </div>
            <button
              onClick={() => setShowNewTicket(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: COLORS.primary,
                color: '#000',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Создать обращение
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => selectTicket(ticket)}
                style={{
                  backgroundColor: COLORS.card,
                  borderRadius: '14px',
                  padding: '16px',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600', flex: 1 }}>
                    {ticket.subject}
                  </div>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '10px',
                    backgroundColor: `${statusColors[ticket.status]}20`,
                    color: statusColors[ticket.status],
                    fontSize: '11px',
                    fontWeight: '600',
                    marginLeft: '8px',
                    flexShrink: 0,
                  }}>
                    {getStatusLabel(ticket.status)}
                  </span>
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px', lineHeight: '1.4' }}>
                  {ticket.messages?.[ticket.messages.length - 1]?.message?.slice(0, 80)}
                  {ticket.messages?.[ticket.messages.length - 1]?.message?.length > 80 && '...'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={12} color={COLORS.textSecondary} />
                  <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                    {new Date(ticket.updated_at).toLocaleString('ru')}
                  </span>
                  {ticket.messages?.length > 1 && (
                    <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                      • {ticket.messages.length} сообщений
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: COLORS.text, marginBottom: '4px' }}>
            Поддержка
          </h1>
          <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Обращения в службу поддержки
          </p>
        </div>
        <button
          onClick={() => setShowNewTicket(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: COLORS.primary,
            color: '#000',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Plus size={20} />
          Новое обращение
        </button>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', gap: '20px', height: 'calc(100vh - 200px)' }}>
        {/* Tickets List */}
        <div style={{
          width: selectedTicket ? '350px' : '100%',
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s',
        }}>
          {/* Help Card inside */}
          <div style={{
            padding: '16px',
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <div style={{
              backgroundColor: '#1e90ff15',
              border: '1px solid #1e90ff30',
              borderRadius: '12px',
              padding: '14px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}>
              <HelpCircle size={20} color="#1e90ff" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>
                  Среднее время ответа: 2 часа
                </div>
              </div>
            </div>
          </div>

          {/* Tickets */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
                Загрузка...
              </div>
            ) : tickets.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <MessageSquare size={40} color={COLORS.textSecondary} style={{ marginBottom: '12px' }} />
                <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
                  Нет обращений
                </div>
              </div>
            ) : (
              tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => selectTicket(ticket)}
                  style={{
                    padding: '16px 20px',
                    borderBottom: `1px solid ${COLORS.border}20`,
                    cursor: 'pointer',
                    backgroundColor: selectedTicket?.id === ticket.id ? COLORS.background : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
                      {ticket.subject}
                    </div>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      backgroundColor: `${statusColors[ticket.status]}20`,
                      color: statusColors[ticket.status],
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>
                      {getStatusLabel(ticket.status)}
                    </span>
                  </div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>
                    {ticket.messages?.[ticket.messages.length - 1]?.message?.slice(0, 60)}...
                  </div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                    {new Date(ticket.updated_at).toLocaleString('ru')}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Panel */}
        {selectedTicket && (
          <div style={{
            flex: 1,
            backgroundColor: COLORS.card,
            borderRadius: '16px',
            border: `1px solid ${COLORS.border}`,
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: `1px solid ${COLORS.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <div style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600' }}>
                  {selectedTicket.subject}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '10px',
                    backgroundColor: `${statusColors[selectedTicket.status]}20`,
                    color: statusColors[selectedTicket.status],
                    fontSize: '12px',
                    fontWeight: '600',
                  }}>
                    {getStatusLabel(selectedTicket.status)}
                  </span>
                  <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                    Создан: {new Date(selectedTicket.created_at).toLocaleString('ru')}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer', padding: '4px' }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
              {selectedTicket.messages?.map((msg, i) => {
                const isUser = msg.sender_role === 'user';
                return (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: isUser ? 'flex-end' : 'flex-start',
                    marginBottom: '16px',
                  }}>
                    <div style={{
                      maxWidth: '70%',
                      padding: '14px 18px',
                      backgroundColor: isUser ? COLORS.primary : COLORS.background,
                      color: isUser ? '#000' : COLORS.text,
                      borderRadius: '16px',
                      borderBottomRightRadius: isUser ? '4px' : '16px',
                      borderBottomLeftRadius: isUser ? '16px' : '4px',
                    }}>
                      {!isUser && (
                        <div style={{ fontSize: '12px', color: '#0ecb81', marginBottom: '6px', fontWeight: '600' }}>
                          Служба поддержки
                        </div>
                      )}
                      <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                        {msg.message}
                      </div>
                      <div style={{ fontSize: '11px', opacity: 0.7, textAlign: 'right', marginTop: '6px' }}>
                        {new Date(msg.created_at).toLocaleString('ru', { 
                          day: '2-digit', 
                          month: '2-digit',
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply */}
            {selectedTicket.status !== 'closed' ? (
              <div style={{
                padding: '16px 20px',
                borderTop: `1px solid ${COLORS.border}`,
                display: 'flex',
                gap: '12px',
              }}>
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Напишите сообщение..."
                  style={{
                    flex: 1,
                    padding: '14px 18px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '12px',
                    color: COLORS.text,
                    fontSize: '14px',
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && sendReply()}
                />
                <button
                  onClick={sendReply}
                  disabled={!replyText.trim() || sending}
                  style={{
                    padding: '14px 24px',
                    backgroundColor: replyText.trim() ? COLORS.primary : COLORS.border,
                    border: 'none',
                    borderRadius: '12px',
                    color: replyText.trim() ? '#000' : COLORS.textSecondary,
                    cursor: replyText.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '600',
                  }}
                >
                  <Send size={18} />
                  Отправить
                </button>
              </div>
            ) : (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: COLORS.textSecondary,
                fontSize: '14px',
                borderTop: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.background,
              }}>
                Тикет закрыт. Если проблема не решена, создайте новое обращение.
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
        }}>
          <div style={{
            backgroundColor: COLORS.card,
            borderRadius: '20px',
            width: '100%',
            maxWidth: '500px',
            padding: '28px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ color: COLORS.text, fontSize: '20px', fontWeight: '600', margin: 0 }}>
                Новое обращение
              </h3>
              <button
                onClick={() => setShowNewTicket(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={24} color={COLORS.textSecondary} />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
                Тема обращения *
              </label>
              <input
                type="text"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Например: Проблема с выводом средств"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  color: COLORS.text,
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
                Сообщение *
              </label>
              <textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                placeholder="Подробно опишите вашу проблему или вопрос..."
                rows={5}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  color: COLORS.text,
                  fontSize: '14px',
                  resize: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowNewTicket(false)}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: COLORS.background,
                  color: COLORS.text,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={createTicket}
                disabled={!newTicket.subject.trim() || !newTicket.message.trim() || sending}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: (newTicket.subject.trim() && newTicket.message.trim()) ? COLORS.primary : COLORS.border,
                  color: (newTicket.subject.trim() && newTicket.message.trim()) ? '#000' : COLORS.textSecondary,
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: (newTicket.subject.trim() && newTicket.message.trim()) ? 'pointer' : 'not-allowed',
                }}
              >
                {sending ? t('loading') : t('submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
